import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { MAX_FILE_SIZE_MB } from '../../../../config/upload';
import { useNotification } from '../../../../hooks/use-notification';
import {
  DocumentSignatureService,
  type IDocumentSignatureService,
  type SignaturePosition,
} from '../../../../services/document-signature';
import {
  type INonConformityDocumentService,
  isNcSignatureRequiredError,
  type NonConformityDocumentResponse,
  NonConformityDocumentService,
  type NonConformitySourceType,
} from '../../../../services/non-conformity-document/non-conformity-document.service';
import { getHttpErrorStatus, resolveApiErrorMessage } from '../../../../utils/error.utils';
import { documentsArchiveQueryKeys } from '../../../documents-archive/query-keys';
import { haccpDashboardQueryKeys } from '../../../haccp-dashboard/query-keys';
import { openNonConformityDaysQueryKeys } from './use-open-non-conformity-days';

export const nonConformityQueryKeys = {
  all: ['non-conformity-documents'] as const,
  list: (sourceType: string, sourceId: string) =>
    ['non-conformity-documents', sourceType, sourceId] as const,
  recheckStatus: (sourceType: string, sourceId: string) =>
    ['non-conformity-follow-ups', sourceType, sourceId] as const,
};

/**
 * Fase 3 part A ("ricontrollo"): stato derivato del ricontrollo per la NC di
 * questa fonte — badge nella sezione documenti, mai bloccante. `enabled`
 * lascia al chiamante decidere quando la NC è effettivamente chiusa (nessun
 * senso interrogare lo stato prima che esista un verbale collegato).
 */
/**
 * Fase 3 part A ("ricontrollo"): deep link dal promemoria (`?ncFollowUp=<id>`)
 * — chiama `onDeepLink` una volta quando il parametro è presente, così la
 * sezione NC del modulo forza l'apertura anche quando `hasDocs` l'avrebbe
 * lasciata collassata di default. Estratto in un hook dedicato per non far
 * contare i suoi 3 hook (query params + effect) nella complessità cognitiva
 * di `NonConformityDocumentSection`.
 */
export function useNcFollowUpDeepLink(onDeepLink: () => void): void {
  const [searchParams] = useSearchParams();
  const ncFollowUpId = searchParams.get('ncFollowUp');
  useEffect(() => {
    if (ncFollowUpId) {
      onDeepLink();
    }
  }, [ncFollowUpId, onDeepLink]);
}

export function useNcRecheckStatus(
  sourceType: NonConformitySourceType,
  sourceId: string,
  enabled = true,
) {
  const service = useInjection<INonConformityDocumentService>(NonConformityDocumentService);
  return useQuery({
    queryKey: nonConformityQueryKeys.recheckStatus(sourceType, sourceId),
    queryFn: () => service.findRecheckStatus(sourceType, sourceId),
    enabled,
  });
}

/** Variabili dell'upload su NC: file + scelta obbligatoria di firma (T0.5). */
export interface NcUploadVariables {
  file: File;
  /**
   * `true` = dichiarazione "già firmato su carta" (upload già SIGNED);
   * `false` = "firma ora": upload PENDING + firma grafica subito dopo.
   */
  alreadySigned: boolean;
}

/** Documento selezionato dall'archivio con il suo stato di firma. */
export interface NcPickerSelection {
  id: string;
  name: string;
  status: 'PENDING' | 'SIGNED';
}

/** Variabili dell'attach da archivio: selezione + scelta per i PENDING. */
export interface NcAttachVariables {
  items: NcPickerSelection[];
  /** Come gestire i documenti PENDING selezionati. */
  pendingChoice: 'sign-now' | 'declare-signed';
}

/**
 * Variabili della sostituzione: un solo documento sostituisce quello già
 * collegato (vincolo "una sola azione correttiva per sorgente"). A differenza
 * di upload/attach, la firma è applicata dal BACKEND nella stessa chiamata:
 * nessun secondo giro client-side su `signDocument`.
 */
export type NcReplaceVariables =
  | { mode: 'upload'; file: File; alreadySigned: boolean }
  | { mode: 'existing'; signedDocumentId: string; alreadySigned?: boolean };

/**
 * Semplificazione accettata per il contesto NC (T0.5): la firma grafica viene
 * applicata in prima pagina, in basso a destra, con le stesse dimensioni
 * percentuali di default del flusso document-signing (20×8).
 */
export const NC_DEFAULT_SIGNATURE_POSITION: SignaturePosition = {
  pageNumber: 1,
  x: 72,
  y: 86,
  width: 20,
  height: 8,
};

/**
 * L'upload PENDING può riuscire e fallire SOLO la firma grafica successiva:
 * questo marker permette all'`onError` di distinguere i due casi e mostrare il
 * messaggio giusto (il documento resta collegato in stato "Da firmare").
 */
class NcSignAfterUploadError extends Error {
  constructor(readonly signError: unknown) {
    super('NC graphic signature failed');
    this.name = 'NcSignAfterUploadError';
  }
}

interface UseNonConformityDocumentsOptions {
  /** Chiamato dopo un upload completato ( firma inclusa, se richiesta ). */
  onUploadSuccess?: () => void;
  /**
   * Upload PENDING riuscito ma firma grafica fallita: il documento È collegato
   * in stato "Da firmare" — la UI chiude il dialog (un retry ri-caricherebbe
   * un duplicato) e rimanda alla firma dall'area Documenti.
   */
  onUploadSignError?: () => void;
  /** Chiamato dopo un attach da archivio completato. */
  onAttachSuccess?: () => void;
  /** Chiamato dopo una sostituzione completata. */
  onReplaceSuccess?: () => void;
  /**
   * 404 `nothingToReplace`: il documento da sostituire è sparito nel
   * frattempo (es. rimosso da un'altra sessione). La UI non ha più nulla da
   * sostituire — il chiamante chiude i dialog aperti, `invalidateAll()` la
   * riallinea (torna al flusso di primo upload).
   */
  onReplaceNothingToReplace?: () => void;
}

export function useNonConformityDocuments(
  sourceType: NonConformitySourceType,
  sourceId: string,
  recordDate: string,
  options: UseNonConformityDocumentsOptions = {},
) {
  const service = useInjection<INonConformityDocumentService>(NonConformityDocumentService);
  const documentSignatureService =
    useInjection<IDocumentSignatureService>(DocumentSignatureService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('nonConformity');
  const { t: tCommon } = useTranslation();
  const [signatureRequired, setSignatureRequired] = useState(false);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: nonConformityQueryKeys.list(sourceType, sourceId),
    queryFn: () => service.findBySource(sourceType, sourceId),
  });

  function invalidateAll() {
    queryClient.invalidateQueries({
      queryKey: nonConformityQueryKeys.list(sourceType, sourceId),
    });
    // La firma cambia lo stato dei documenti: invalidiamo anche l'archivio
    // documenti (badge PENDING/SIGNED) oltre alle key del feed NC.
    queryClient.invalidateQueries({ queryKey: ['tenant-documents'] });
    queryClient.invalidateQueries({ queryKey: documentsArchiveQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: haccpDashboardQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: openNonConformityDaysQueryKeys.all });
  }

  function handleMutationError(err: unknown): void {
    if (isNcSignatureRequiredError(err)) {
      // 422 { code: 'nc_document_signature_required' }: la scelta va ripetuta,
      // la UI riapre il dialog (flag `signatureRequired`) senza chiudere nulla.
      setSignatureRequired(true);
      showError(t('signatureRequired'));
      return;
    }
    if (getHttpErrorStatus(err) === 413) {
      showError(tCommon('common.fileErrors.fileTooLargeServer', { maxSize: MAX_FILE_SIZE_MB }));
      return;
    }
    showError(resolveApiErrorMessage(err, t));
  }

  const uploadMutation = useMutation({
    mutationFn: async (vars: NcUploadVariables) => {
      const link = await service.uploadAndAttach(sourceType, sourceId, recordDate, vars.file, {
        alreadySigned: vars.alreadySigned,
      });
      if (!vars.alreadySigned) {
        // "Firma ora in Prospero": il PDF è PENDING, applichiamo subito la
        // firma grafica (prima pagina, posizione default) così la NC si chiude.
        try {
          await documentSignatureService.signDocument(link.signedDocumentId, [
            NC_DEFAULT_SIGNATURE_POSITION,
          ]);
        } catch (signErr) {
          throw new NcSignAfterUploadError(signErr);
        }
      }
      return link;
    },
    onMutate: () => setSignatureRequired(false),
    onSuccess: (_link, vars) => {
      invalidateAll();
      showSuccess(vars.alreadySigned ? t('uploadSuccess') : t('uploadAndSignSuccess'));
      options.onUploadSuccess?.();
    },
    onError: (err: unknown) => {
      if (err instanceof NcSignAfterUploadError) {
        showError(t('signNowError'));
        invalidateAll();
        options.onUploadSignError?.();
        return;
      }
      handleMutationError(err);
    },
  });

  const attachMutation = useMutation({
    mutationFn: async (vars: NcAttachVariables) => {
      const pending = vars.items.filter((item) => item.status === 'PENDING');
      if (vars.pendingChoice === 'sign-now' && pending.length > 0) {
        // Prima la firma grafica (il gate T0.4 risponde 422 su attach PENDING
        // non dichiarato), poi l'attach di tutti i selezionati.
        try {
          await Promise.all(
            pending.map((item) =>
              documentSignatureService.signDocument(item.id, [NC_DEFAULT_SIGNATURE_POSITION]),
            ),
          );
        } catch (signErr) {
          throw new NcSignAfterUploadError(signErr);
        }
      }
      return Promise.all(
        vars.items.map((item) =>
          service.attach({
            signedDocumentId: item.id,
            sourceType,
            sourceId,
            recordDate,
            alreadySigned:
              item.status === 'PENDING' && vars.pendingChoice === 'declare-signed'
                ? true
                : undefined,
          }),
        ),
      );
    },
    onMutate: () => setSignatureRequired(false),
    onSuccess: () => {
      invalidateAll();
      showSuccess(t('attachSuccess'));
      options.onAttachSuccess?.();
    },
    onError: (err: unknown) => {
      if (err instanceof NcSignAfterUploadError) {
        showError(t('signNowError'));
        invalidateAll();
        return;
      }
      handleMutationError(err);
      invalidateAll();
    },
  });

  const replaceMutation = useMutation({
    mutationFn: async (vars: NcReplaceVariables) => {
      if (vars.mode === 'upload') {
        return service.replaceWithUpload(sourceType, sourceId, recordDate, vars.file, {
          alreadySigned: vars.alreadySigned,
        });
      }
      return service.replaceWithExisting({
        signedDocumentId: vars.signedDocumentId,
        sourceType,
        sourceId,
        recordDate,
        alreadySigned: vars.alreadySigned,
      });
    },
    onMutate: () => setSignatureRequired(false),
    onSuccess: () => {
      invalidateAll();
      showSuccess(t('replaceSuccess'));
      options.onReplaceSuccess?.();
    },
    onError: (err: unknown) => {
      // 404 sul contratto di replace ha un solo significato: non c'e' piu'
      // nulla da sostituire (rimosso nel frattempo). Riallineiamo la UI
      // invece di mostrare l'errore generico.
      if (getHttpErrorStatus(err) === 404) {
        showError(t('replaceNothingToReplace'));
        invalidateAll();
        options.onReplaceNothingToReplace?.();
        return;
      }
      handleMutationError(err);
    },
  });

  const detachMutation = useMutation({
    mutationFn: (id: string) => service.detach(id),
    onSuccess: () => {
      invalidateAll();
      showSuccess(t('detachSuccess'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });

  return {
    documents,
    isLoading,
    upload: uploadMutation.mutate,
    attach: attachMutation.mutate,
    replace: replaceMutation.mutate,
    detach: detachMutation.mutate,
    isUploading: uploadMutation.isPending,
    isAttaching: attachMutation.isPending,
    isReplacing: replaceMutation.isPending,
    isDetaching: detachMutation.isPending,
    /** True dopo un 422 firma-richiesta: la UI riapre il dialog di scelta. */
    signatureRequired,
  };
}

export type { NonConformityDocumentResponse };
